import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Instagram, Settings, X, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Video Asset - Transparent WebM
const PIRONE_VIDEO = "/assets/pirone-logo-video.webm";

const Footer = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState({ username: '', password: '' });
  const [adminError, setAdminError] = useState('');

  // Links are built from common translations
  const links = [
    { label: t('common.services'), href: '/services' },
    { label: t('common.pricing'), href: '/pricing' },
    { label: t('common.about'), href: '/about' },
    { label: t('common.contact'), href: '/contact' },
  ];

  // Admin login handler
  const handleAdminLogin = (e) => {
    e.preventDefault();
    // Check against stored or default credentials
    const storedUsername = localStorage.getItem('adminUsername') || 'pirone';
    const storedPassword = localStorage.getItem('adminPassword') || 'admin2026';
    
    if (adminCredentials.username === storedUsername && adminCredentials.password === storedPassword) {
      localStorage.setItem('adminAuth', 'true');
      setShowAdminModal(false);
      setAdminError('');
      navigate('/admin');
    } else {
      setAdminError(t('footer.adminError'));
    }
  };

  return (
    <>
      <footer className="relative z-10 bg-[#030303] border-t border-white/[0.05]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* Main Footer */}
          <div className="py-16 lg:py-20 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-5">
              <Link to="/" className="inline-block mb-6">
                <video 
                  src={PIRONE_VIDEO}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="h-6 w-auto"
                  style={{ mixBlendMode: 'lighten', background: 'transparent' }}
                />
              </Link>
              <p className="text-white/40 text-sm leading-relaxed max-w-[380px] mb-8">
                {t('footer.manifesto')}
              </p>
              
              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.instagram.com/pirone.media/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-white/40 hover:text-[#E4405F] hover:border-[#E4405F]/30 hover:bg-[#E4405F]/10 transition-all duration-300"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://wa.me/15513548697"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 h-11 px-5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300 font-medium text-sm"
                  aria-label="WhatsApp"
                  data-testid="footer-whatsapp-btn"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" className="transition-transform group-hover:scale-110">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="hidden sm:inline">WhatsApp</span>
                </a>
              </div>
            </div>

            {/* Links Column */}
            <div className="col-span-1 lg:col-span-2">
              <h4 className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 mb-6">
                {t('footer.linksTitle')}
              </h4>
              <ul className="space-y-3">
                {links.map((item) => (
                  <li key={item.href}>
                    <Link 
                      to={item.href}
                      className="text-white/50 text-sm hover:text-white transition-colors duration-300"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Empty space for alignment */}
            <div className="col-span-1 lg:col-span-2"></div>

            {/* Contact Column */}
            <div className="col-span-2 md:col-span-1 lg:col-span-3">
              <h4 className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 mb-6">
                {t('footer.contactTitle')}
              </h4>
              <a 
                href="mailto:info@pirone.io"
                className="text-white hover:text-[#38BDF8] transition-colors duration-300 text-lg font-medium block"
              >
                info@pirone.io
              </a>
              <a 
                href="tel:+15513548697"
                className="text-white/50 hover:text-[#38BDF8] transition-colors duration-300 text-sm mt-2 block"
              >
                +1 (551) 354-8697
              </a>
              
              {/* CTA */}
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 h-10 px-5 bg-white/5 border border-white/[0.08] text-white text-sm font-medium rounded-full hover:bg-white/10 hover:border-white/15 transition-all duration-300"
                >
                  <span>{t('common.startProject')}</span>
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="py-6 border-t border-white/[0.04] flex flex-col md:flex-row justify-between items-center gap-4">
            <span className="text-white/30 text-xs">
              {t('footer.rights')}
            </span>
            <div className="flex items-center gap-6">
              {/* Admin Settings Button */}
              <button 
                onClick={() => setShowAdminModal(true)}
                className="text-white/20 hover:text-white/40 transition-colors duration-300"
                aria-label="Admin"
              >
                <Settings size={14} />
              </button>
              <a href="/privacy" className="text-white/30 hover:text-white/60 text-xs transition-colors duration-300">
                {t('footer.privacy')}
              </a>
              <a href="/privacy" className="text-white/30 hover:text-white/60 text-xs transition-colors duration-300">
                {t('footer.terms')}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Login Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowAdminModal(false)}
          />
          <div className="relative w-full max-w-sm bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <button
              onClick={() => setShowAdminModal(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center mx-auto mb-4">
                <Lock size={20} className="text-[#38BDF8]" />
              </div>
              <h3 className="text-lg font-semibold text-white">{t('footer.adminTitle')}</h3>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider">{t('footer.adminUsername')}</label>
                <input
                  type="text"
                  value={adminCredentials.username}
                  onChange={(e) => setAdminCredentials({ ...adminCredentials, username: e.target.value })}
                  className="w-full mt-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider">{t('footer.adminPassword')}</label>
                <input
                  type="password"
                  value={adminCredentials.password}
                  onChange={(e) => setAdminCredentials({ ...adminCredentials, password: e.target.value })}
                  className="w-full mt-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50"
                  required
                />
              </div>

              {adminError && (
                <p className="text-red-400 text-sm text-center">{adminError}</p>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-[#38BDF8] text-black font-semibold rounded-lg hover:bg-white transition-colors"
              >
                {t('footer.adminLogin')}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
